import { X_EB_ORIGIN_BUCKET } from '../../lib/cookies/constants';

export type Cohort = 'old' | 'new' | 'unavailable';

/**
 * The cohort (OLD/NEW) a request was routed to, read from the edge-stamped
 * `x-eb-origin-bucket` header.
 *
 * Trust boundary: this value is trusted ONLY because the parent edge's
 * viewer-request function unconditionally strips any viewer-supplied value and
 * re-stamps `old`/`new` before the cache lookup (see
 * docs/consolidation/folds/homepage-traffic-seam.md §2). The host proxy strips
 * the inbound header whole (lib/cookies/constants.ts isProxyManagedHeader) and
 * `buildForwardedHeaders` never re-adds it, so a value that reaches a handler
 * can only come from a trusted upstream app (or a test).
 *
 * The return is bounded to the three documented values — anything else, an
 * absent header, an array, or a non-string is `unavailable` (never echoed), so
 * a user-injected string can never reach a log or span as a high-cardinality
 * tag.
 */
export function resolveCohort(value: unknown): Cohort {
  const resolved = Array.isArray(value) ? value[0] : value;
  return resolved === 'new' || resolved === 'old' ? resolved : 'unavailable';
}

/** Read and validate the cohort off a Next/Node header bag (case-insensitive). */
export function cohortFromHeaders(
  headers: { get?: (name: string) => string | null; [key: string]: unknown } | undefined,
): Cohort {
  if (!headers) return 'unavailable';
  if (typeof headers.get === 'function') {
    return resolveCohort(headers.get(X_EB_ORIGIN_BUCKET));
  }
  const direct = headers[X_EB_ORIGIN_BUCKET] ?? headers[X_EB_ORIGIN_BUCKET.toLowerCase()];
  return resolveCohort(direct);
}
